import React, { useEffect, useState, Fragment } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { connect } from 'react-redux';
import { getProductPackages, deleteProductPackage } from '../../actions/productPackage';
import { Plus, Search, Edit, Trash2, Package, AlertTriangle, ShoppingBag } from 'lucide-react';

const Packages = ({ getProductPackages, deleteProductPackage, packages, loading }) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [packageToDelete, setPackageToDelete] = useState(null);
    const [filteredPackages, setFilteredPackages] = useState([]);

    useEffect(() => {
        document.title = 'Product Packages';
        getProductPackages('?packageType=all');
    }, []);

    useEffect(() => {
        if (packages) {
            const filtered = packages.filter(pkg =>
                pkg.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                pkg.packageContent?.some(content =>
                    content.toLowerCase().includes(searchTerm.toLowerCase())
                )
            );
            setFilteredPackages(filtered);
        }
    }, [packages, searchTerm]);

    const handleDeleteClick = (pkg) => {
        setPackageToDelete(pkg);
        setShowDeleteModal(true);
    };

    const confirmDelete = () => {
        if (packageToDelete) {
            deleteProductPackage(packageToDelete._id);
            setShowDeleteModal(false);
            setPackageToDelete(null);
        }
    };

    const cancelDelete = () => {
        setShowDeleteModal(false);
        setPackageToDelete(null);
    };

    const getQuantityDisplay = (pkg) => {
        if (pkg.maxQuantity === 0) {
            return `Min: ${pkg.minQuantity}, Max: Unlimited`;
        }
        return `${pkg.minQuantity} - ${pkg.maxQuantity}`;
    };

    return (
        <Fragment>
            <div className="container-fluid py-4">
                {/* Header Section */}
                <div className="row mb-4">
                    <div className="col-12">
                        <div className="d-flex justify-content-between align-items-center flex-wrap">
                            <div>
                                <h1 className="h3 mb-2 text-gray-800 fw-bold">
                                    <Package size={32} className="me-3 text-primary" />
                                    Product Packages
                                </h1>
                                <p className="text-muted mb-0">
                                    Manage your product packages and bundle configurations
                                </p>
                            </div>
                            <Link
                                to="/inventory/v1.2/product-package/new"
                                className="btn btn-primary btn-lg shadow-sm d-flex align-items-center"
                            >
                                <Plus size={20} className="me-2" />
                                Create New Package
                            </Link>
                        </div>
                    </div>
                </div>

                {/* Search and Stats Section */}
                <div className="row mb-4">
                    <div className="col-lg-6">
                        <div className="card shadow-sm">
                            <div className="card-body">
                                <div className="input-group">
                                    <span className="input-group-text bg-light border-end-0">
                                        <Search size={18} className="text-muted" />
                                    </span>
                                    <input
                                        type="text"
                                        className="form-control border-start-0"
                                        placeholder="Search packages by name or content..."
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="col-lg-6">
                        <div className="card shadow-sm bg-success text-white">
                            <div className="card-body">
                                <div className="d-flex justify-content-between align-items-center">
                                    <div>
                                        <div className="h4 mb-0">{packages?.length || 0}</div>
                                        <div className="small">Total Packages</div>
                                    </div>
                                    <ShoppingBag size={32} className="opacity-75" />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Packages Grid */}
                {loading ? (
                    <div className="text-center py-5">
                        <div className="spinner-border text-primary" role="status">
                            <span className="visually-hidden">Loading...</span>
                        </div>
                        <p className="mt-3 text-muted">Loading packages...</p>
                    </div>
                ) : filteredPackages && filteredPackages.length > 0 ? (
                    <div className="row">
                        {filteredPackages.map((pkg) => (
                            <div key={pkg._id} className="col-xl-4 col-lg-6 col-md-6 mb-4">
                                <div
                                    className="card shadow-sm border-0 h-100"
                                    style={{
                                        borderRadius: '0.75rem',
                                        transition: 'all 0.3s ease'
                                    }}
                                    onMouseEnter={(e) => {
                                        e.currentTarget.style.transform = 'translateY(-2px)';
                                        e.currentTarget.style.boxShadow = '0 0.5rem 1rem rgba(0, 0, 0, 0.15)';
                                    }}
                                    onMouseLeave={(e) => {
                                        e.currentTarget.style.transform = 'translateY(0)';
                                        e.currentTarget.style.boxShadow = '';
                                    }}
                                >
                                    <div className="card-header bg-white border-bottom-0 pb-0">
                                        <div className="d-flex justify-content-between align-items-start">
                                            <div className="flex-grow-1">
                                                <h5 className="card-title mb-1 text-primary fw-bold">
                                                    {pkg.name}
                                                </h5>
                                                <div className="d-flex flex-wrap gap-1 mb-2">
                                                    {pkg.allowCombo && (
                                                        <span className="badge bg-success" style={{ fontSize: '0.75rem' }}>
                                                            Combo Allowed
                                                        </span>
                                                    )}
                                                    {pkg.oneTimePurchase && (
                                                        <span className="badge bg-warning" style={{ fontSize: '0.75rem' }}>
                                                            One-Time Purchase
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                            <div className="btn-group">
                                                <Link
                                                    to={`/inventory/v1.2/product-package/${pkg._id}`}
                                                    className="btn btn-sm btn-outline-primary"
                                                    title="Edit Package"
                                                >
                                                    <Edit size={14} />
                                                </Link>
                                                <button
                                                    className="btn btn-sm btn-outline-danger"
                                                    onClick={() => handleDeleteClick(pkg)}
                                                    title="Delete Package"
                                                >
                                                    <Trash2 size={14} />
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="card-body pt-2">
                                        {/* Package Content */}
                                        <div className="mb-3">
                                            <h6 className="text-muted mb-2 small">Package Contents:</h6>
                                            <div className="d-flex flex-wrap gap-1">
                                                {pkg.packageContent?.map((content, index) => (
                                                    <span key={index} className="badge bg-light text-dark border">
                                                        {content}
                                                    </span>
                                                ))}
                                            </div>
                                        </div>

                                        {/* Quantity Info */}
                                        <div className="row g-3">
                                            <div className="col-12">
                                                <div className="bg-light rounded p-2">
                                                    <div className="small text-muted mb-1">Quantity Range</div>
                                                    <div className="fw-bold text-info">
                                                        {getQuantityDisplay(pkg)}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Package Features */}
                                        <div className="mt-3 pt-3 border-top">
                                            <div className="row g-2">
                                                <div className="col-6">
                                                    <div className="text-center">
                                                        <div className={`h6 mb-0 ${pkg.allowCombo ? 'text-success' : 'text-muted'}`}>
                                                            {pkg.allowCombo ? '✓' : '✗'}
                                                        </div>
                                                        <div className="small text-muted">Combo</div>
                                                    </div>
                                                </div>
                                                <div className="col-6">
                                                    <div className="text-center">
                                                        <div className={`h6 mb-0 ${pkg.oneTimePurchase ? 'text-warning' : 'text-muted'}`}>
                                                            {pkg.oneTimePurchase ? '✓' : '✗'}
                                                        </div>
                                                        <div className="small text-muted">One-Time</div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="card-footer bg-light border-top-0">
                                        <div className="d-flex justify-content-between align-items-center">
                                            <Link
                                                to={`/inventory/v1.2/product-package/${pkg._id}`}
                                                className="btn btn-primary btn-sm"
                                            >
                                                <Edit size={14} className="me-1" />
                                                Manage
                                            </Link>
                                            <small className="text-muted">
                                                ID: {pkg._id?.slice(-6)}
                                            </small>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className="text-center py-5">
                        <div className="mb-4">
                            <Package size={64} className="text-muted" />
                        </div>
                        <h4 className="text-muted mb-3">
                            {searchTerm ? 'No packages found' : 'No packages created yet'}
                        </h4>
                        <p className="text-muted mb-4">
                            {searchTerm
                                ? `No packages match "${searchTerm}". Try adjusting your search.`
                                : 'Get started by creating your first product package.'
                            }
                        </p>
                        {!searchTerm && (
                            <Link
                                to="/inventory/v1.2/product-package/new"
                                className="btn btn-primary btn-lg"
                            >
                                <Plus size={20} className="me-2" />
                                Create Your First Package
                            </Link>
                        )}
                    </div>
                )}

                {/* Delete Confirmation Modal */}
                {showDeleteModal && (
                    <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                        <div className="modal-dialog modal-dialog-centered">
                            <div className="modal-content">
                                <div className="modal-header border-0">
                                    <h5 className="modal-title d-flex align-items-center text-danger">
                                        <AlertTriangle size={24} className="me-2" />
                                        Confirm Delete
                                    </h5>
                                    <button
                                        type="button"
                                        className="btn-close"
                                        onClick={cancelDelete}
                                    ></button>
                                </div>
                                <div className="modal-body">
                                    <div className="text-center">
                                        <div className="mb-3">
                                            <Trash2 size={48} className="text-danger" />
                                        </div>
                                        <h6 className="mb-3">Delete Package "{packageToDelete?.name}"?</h6>
                                        <p className="text-muted mb-4">
                                            This action cannot be undone. The package and all its configurations
                                            will be permanently deleted.
                                        </p>

                                        {/* Show package contents in modal */}
                                        {packageToDelete?.packageContent && (
                                            <div className="alert alert-info">
                                                <small>
                                                    <strong>Package Contents:</strong><br />
                                                    {packageToDelete.packageContent.join(', ')}
                                                </small>
                                            </div>
                                        )}

                                        <div className="alert alert-warning d-flex align-items-center">
                                            <AlertTriangle size={20} className="me-2" />
                                            <small>
                                                <strong>Warning:</strong> This will permanently delete the package and all its data.
                                            </small>
                                        </div>
                                    </div>
                                </div>
                                <div className="modal-footer border-0">
                                    <button
                                        type="button"
                                        className="btn btn-secondary"
                                        onClick={cancelDelete}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="button"
                                        className="btn btn-danger"
                                        onClick={confirmDelete}
                                    >
                                        <Trash2 size={16} className="me-2" />
                                        Delete Package
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </Fragment>
    );
};

Packages.propTypes = {
    packages: PropTypes.array,
    getProductPackages: PropTypes.func.isRequired,
    deleteProductPackage: PropTypes.func.isRequired,
    loading: PropTypes.bool,
};

const mapStateToProps = state => ({
    packages: state.productPackage.packages,
    loading: state.productPackage.loading
});

export default connect(mapStateToProps, { getProductPackages, deleteProductPackage })(Packages);

